<template>
  <BaseChip
    class="base-value-tag"
    size="sm"
    tone="info"
    preset="soft"
    :clearable="isClearable"
    :selected="selected"
    @click="valueClicked"
    @clear="deleteValue"
  >
    {{ label }}
  </BaseChip>
</template>

<script>
import BaseChip from '@/components/base/BaseChip.vue';

import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'BaseValueTag',
  components: {
    BaseChip,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isClearable: {
      type: Boolean,
      default: true,
    },
    labelAs: {
      type: String,
      default: 'value', // 'value' or 'source'
    },
  },
  data() {
    return {
      selected: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('annotations', ['selectedAnnotation']),
    label() {
      const MAX_CHARS = 500;
      const text = this.labelAs === 'value' ? this.value.value : this.$t(`evaluation-grid.source.${this.value.source}`);
      return text.length > MAX_CHARS ? `${text.slice(0, MAX_CHARS)}...` : text;
    },
  },
  methods: {
    ...mapActions('currentDocument', ['scrollToAnnotation']),
    deleteValue() {
      this.$emit('delete', this.value);
    },
    valueClicked() {
      this.$emit('linked-value-clicked', this.value);
    },
  },
  watch: {
    selectedAnnotation() {
      if (this.selectedAnnotation && this.selectedAnnotation.id === this.value.textAnnotationId) {
        this.selected = true;
      } else {
        this.selected = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.base-value-tag {
  margin: 0.25rem;
}

// Resting state is a flat light-grey fill that does not warm on hover. Retuning
// the chip's own --action-* channels rather than overriding `background-color`
// directly: the primitive reads these to paint both the rest and hover states,
// so one rule covers both and the selected tone is still free to take over.
//
// Scoped to :not(--selected) for two reasons — it structurally yields to the
// selected recipe, and the extra class lifts this above the primitive's base
// rule, which a bare `.base-value-tag` would only tie (leaving the fill to
// depend on stylesheet order).
.base-value-tag.base-chip:not(.base-chip--selected) {
  --action-bg: #{$color-light-grey};
  --action-bg-hover: #{$color-light-grey};
  --action-border: transparent;
}

// The label is rendered by BaseChip, so the plain scoped selector doesn't reach
// it — pierce the scope with ::v-deep.
.base-value-tag ::v-deep .base-chip__label {
  max-width: 334px;
}
</style>
